// frontend-b2b/src/composables/useBrandDetail.js
import { ref, computed } from '@vue/composition-api'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { getDefaultProductImage } from '@/@core/utils/utils'
import { apiToastSuccess } from '@/@core/utils/toast'
import store from '@/store'
import { FETCH_BRANDS_DETAILS_V2, GET_SCHEDULE, FOLLOW_OR_UNFOLLOW_BRAND } from '@/store/modules/brand.module'
import { GET_COLLECTION_LIST } from '@/store/modules/collection.module'

const { formatCurrency } = utils
const { MONTHS, COLLECTION_STATUS, SCHEDULED_COLLECTION_TYPE } = c

export function useBrandDetail() {
  // Brand details state
  const brandDetails = ref({})
  const isLoaded = ref(false)

  // Collections state
  const liveCollections = ref([])
  const previousCollections = ref([])
  const collectionsLoaded = ref(false)

  // Subscription state
  const monthsWithCollection = ref([])
  const isSubscribed = ref(false)
  const scheduledCollectionId = ref(null)

  // Computed
  const brandLogo = computed(() => (brandDetails.value.logo
      ? `${brandDetails.value.logo}?t=${Date.now()}`
      : getDefaultProductImage(400, 400)))

  const hasSocialLinks = computed(() => !!(
      brandDetails.value.facebookLink
      || brandDetails.value.linkedinLink
    ))

  const scheduledCollectionTypes = computed(() => {
    const types = {}
    Object.values(SCHEDULED_COLLECTION_TYPE).forEach(type => {
      types[type.LABEL] = type.TEXT
    })
    return types
  })

  // Methods
  function fetchBrandDetails(brandId) {
    return store.dispatch(FETCH_BRANDS_DETAILS_V2, brandId)
      .then(res => {
        brandDetails.value = res.data
        isLoaded.value = true
        return res.data
      })
      .catch(() => {
        isLoaded.value = true
      })
  }

  function fetchCollections(brandId) {
    const params = { brandId }
    return store.dispatch(GET_COLLECTION_LIST, params)
      .then(res => {
        const collections = res.data.data.collections
        liveCollections.value = collections.filter(
          col => col.status === COLLECTION_STATUS.LIVE.VALUE,
        )
        previousCollections.value = collections.filter(
          col => col.status === COLLECTION_STATUS.EXPIRED.VALUE,
        )
        collectionsLoaded.value = true
      })
      .catch(() => {
        collectionsLoaded.value = true
      })
  }

  function mergeMonthsWithSchedules(schedules) {
    const scheduleByMonth = new Map(schedules.map(s => [s.month, s]))
    return Object.values(MONTHS).map(month => ({
      ...month,
      ...(scheduleByMonth.get(month.LABEL) || {}),
    }))
  }

  function applyScheduleData(res) {
    const data = res.data.data
    const schedules = data?.schedules || []
    scheduledCollectionId.value = data?._id || ''
    isSubscribed.value = data?.isSubscribed || false
    if (schedules.length) {
      monthsWithCollection.value = mergeMonthsWithSchedules(schedules)
    }
  }

  function getSubscription(brandId) {
    return store.dispatch(GET_SCHEDULE, { brandId })
      .then(applyScheduleData)
      .catch(() => { })
  }

  function onHandleSubscription() {
    if (scheduledCollectionId.value) {
      store.dispatch(FOLLOW_OR_UNFOLLOW_BRAND, scheduledCollectionId.value)
        .then(res => {
          isSubscribed.value = !isSubscribed.value
          apiToastSuccess(res?.data?.message || 'Subscription updated')
        })
        .catch(() => { })
    }
  }

  function textTruncate(string) {
    return string.substr(0, 150)
  }

  return {
    // State
    brandDetails,
    isLoaded,
    liveCollections,
    previousCollections,
    collectionsLoaded,
    monthsWithCollection,
    isSubscribed,
    scheduledCollectionId,

    // Computed
    brandLogo,
    hasSocialLinks,
    scheduledCollectionTypes,

    // Methods
    fetchBrandDetails,
    fetchCollections,
    getSubscription,
    onHandleSubscription,
    textTruncate,

    // Re-exports
    formatCurrency,
    MONTHS,
    COLLECTION_STATUS,
  }
}
